import React, {useEffect, useMemo, useRef, useState} from "react";
import {useSelector, useDispatch} from "react-redux";
import {format} from "date-fns";
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import ICONS from "connectui/icons/iconMap";

import {createAction} from "redux/actions/experiencesActions";
import {editMessages} from "redux/actions/messagesActions";
import {
  closeGeneralError,
  closeGeneralSuccessAlert,
  openGeneralError,
  openGeneralSuccessAlert,
} from "redux/actions/settingsActions";

import FlexContainer from "connectui/component/layout/FlexContainer";
import Skeleton from "connectui/component/display/Skeleton";
import Text from "connectui/component/display/Text";
import ProfilePicture from "connectui/component/display/ProfilePicture";
import SvgIcon from "connectui/component/display/SvgIcon";
import ContextMenu from "connectui/component/input/context/ContextMenu";
import TextInput from "connectui/component/input/text/type/TextInput";
import ButtonText from "connectui/component/input/button/ButtonText";
import OutlinedButtonBase from "connectui/component/input/button/base/OutlinedButtonBase";
import {useTranslation} from "react-i18next";

const useStyles = (theme) =>
  buildStyles({
    container: {
      width: "100%",
      height: "100%",
      padding: theme.spacing(5, 4),
      overflow: "auto",
      backgroundColor: theme.colors.gray50,
      "&::-webkit-scrollbar": {width: "0!important"},
    },
    note: {
      backgroundColor: theme.colors.white,
      borderRadius: `${theme.radius.lg}px`,
      padding: theme.spacing(3, 4),
      boxShadow: theme.shadows.xs,
      width: "100%",
    },
    moreIcon: {
      width: "20px",
      height: "20px",
      borderRadius: `${theme.radius.sm}px`,
      backgroundColor: theme.colors.gray50,
      border: `1px solid ${theme.colors.gray200}`,
      boxShadow: theme.shadows.xs,
      cursor: "pointer",
      padding: "1px",
    },
  });

const Note = ({note, openMenu, isEditing, cancel, save}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const {t} = useTranslation();
  const user_profile = useSelector(
    (state) => state.defaultReducer.user_profile,
  );
  const [noteMessage, setNoteMessage] = useState("");
  const sentBy =
    user_profile?.enso_users?.find(
      (eu) => eu.enso_user_id === note.enso_user_id,
    )?.name ?? user_profile?.name;
  const isDeleted = note.message === `--deleted-note_${note.sent}--`;

  useEffect(() => {
    if (isEditing) {
      setNoteMessage(note.message);
    }
  }, [isEditing]);

  return (
    <FlexContainer
      flexDirection="column"
      gap={theme.spacing(3)}
      className={classes.note}
      sx={{opacity: isDeleted ? 0.5 : 1}}
    >
      <FlexContainer
        alignItems="center"
        gap={theme.spacing(1.5)}
        sx={{width: "100%"}}
      >
        <ProfilePicture
          sx={{width: "20px", height: "20px", minWidth: "20px"}}
          name={sentBy}
          textProps={{variant: "textXXS"}}
          svgIcon={!note.enso_user_id ? ICONS["file-import"] : undefined}
        />
        <Text variant="textSM" weight="medium" color="gray700" sx={{flex: 1}}>
          {!note.enso_user_id ? "Imported from PMS" : sentBy}
        </Text>
        <Text variant="textXS" weight="medium" color="textSecondary">
          {`${format(note.sent, "MMMM d, y")} at ${format(note.sent, "p")}`}
        </Text>
        {!isDeleted && (
          <SvgIcon
            component={ICONS["more-vertical"]}
            className={classes.moreIcon}
            onClick={openMenu}
          />
        )}
      </FlexContainer>
      <FlexContainer
        flexDirection="column"
        gap={theme.spacing(2)}
        sx={{width: "100%"}}
      >
        {isEditing && !isDeleted ? (
          <>
            <TextInput
              as={"textarea"}
              inputProps={{
                value: noteMessage,
                onChange: (e) => setNoteMessage(e.target.value),
                placeholder: "",
                style: {
                  ...theme.typography.textSM.regular,
                  color: theme.colors.gray700,
                  resize: "none",
                  cursor: "text",
                  width: "100%",
                  height: "100%",
                  backgroundColor: theme.colors.gray50,
                  borderRadius: `${theme.radius.sm}px`,
                  padding: theme.spacing(2),
                },
              }}
              sx={{width: "100%", height: "100px"}}
            />
            <FlexContainer
              justifyContent="flex-end"
              gap={theme.spacing(2)}
              sx={{width: "100%"}}
            >
              <ButtonText
                buttonBase={OutlinedButtonBase}
                label="Cancel"
                onClick={cancel}
                sx={{height: "40px"}}
              />
              <ButtonText
                label="Save"
                onClick={() => save(noteMessage)}
                variant="textSM"
                color="white"
                sx={{height: "40px"}}
              />
            </FlexContainer>
          </>
        ) : (
          <Text
            variant="textSM"
            sx={{
              fontStyle: isDeleted ? "italic" : "normal",
              whiteSpace: "break-spaces",
            }}
          >
            {isDeleted
              ? t("inbox.conversations.notes.note-deleted")
              : note.message}
          </Text>
        )}
      </FlexContainer>
    </FlexContainer>
  );
};

const NotesPanel = ({
  notes = [],
  setNotes,
  skeleton = {enabled: false},
  guestId,
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const clickedNote = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const descendingNotes = useMemo(() => [...notes].reverse(), [notes]);

  const openMenu = (note) => (e) => {
    clickedNote.current = note;
    setAnchorEl(e.currentTarget);
  };
  const closeMenu = () => {
    setAnchorEl(null);
  };

  const updateNote = ({remove = false, newContent = ""}) => {
    const prevNote = remove ? {...clickedNote.current} : {...selectedNote};
    const body = {
      action_id: "update_message",
      guest_id: guestId,
      body: {
        ...prevNote,
        message: remove ? `--deleted-note_${prevNote.sent}--` : newContent,
      },
    };

    dispatch(editMessages({...body.body}, prevNote.id));
    setNotes(
      notes.map((note) =>
        note.id === prevNote.id ? {...note, ...body.body} : note,
      ),
    );

    dispatch(
      createAction({
        action: body,
        guestId,
        disableAlerts: true,
        onError: () => {
          dispatch(editMessages({...prevNote}, prevNote.id));
          setNotes(
            notes.map((note) => (note.id === prevNote.id ? prevNote : note)),
          );
        },
      }),
    );
    setSelectedNote(null);
  };

  return (
    <FlexContainer
      flexDirection="column"
      gap={theme.spacing(8)}
      className={classes.container}
    >
      <ContextMenu
        open={!!anchorEl}
        onClose={closeMenu}
        anchorEl={anchorEl}
        modalProps={{
          modalPosition: {horizontal: "end"},
        }}
        menuItems={[
          {
            label: "Edit Note",
            icon: ICONS.edit,
            onClick: () => {
              setSelectedNote(clickedNote.current);
              closeMenu();
            },
          },
          {
            label: "Delete",
            icon: ICONS.trash,
            iconProps: {sx: {color: theme.colors.error}},
            labelProps: {color: "error"},
            onClick: () => {
              updateNote({remove: true});
              closeMenu();
            },
          },
        ]}
      />
      {skeleton.enabled ? (
        <FlexContainer
          flexDirection="column"
          gap={theme.spacing(2)}
          sx={{width: "100%"}}
        >
          {Array.from({length: 6}).map((_, index) => (
            <Skeleton
              key={index}
              width={"100%"}
              height={"80px"}
              style={{borderRadius: `${theme.radius.lg}px`}}
            />
          ))}
        </FlexContainer>
      ) : !notes.length ? (
        <Text
          variant="textLG"
          color="gray400"
          sx={{
            width: "100%",
            height: "100%",
            textAlign: "center",
            alignContent: "center",
          }}
        >
          {t("inbox.conversations.notes.empty-list")}
        </Text>
      ) : (
        <FlexContainer
          flexDirection="column"
          gap={theme.spacing(4)}
          sx={{width: "100%", height: "100%"}}
        >
          {descendingNotes.map((note) => (
            <FlexContainer
              key={note.id}
              flexDirection="column"
              sx={{width: "100%"}}
            >
              <Note
                note={note}
                isEditing={selectedNote?.id === note.id}
                cancel={() => setSelectedNote(null)}
                save={(newContent) => updateNote({newContent})}
                openMenu={openMenu(note)}
              />
            </FlexContainer>
          ))}
        </FlexContainer>
      )}
    </FlexContainer>
  );
};

export default NotesPanel;
